import { Card } from "@/components/ui/card";
import { Fragment } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Users, UserPlus, TrendingDown, Clock } from "lucide-react";

function KpiCard({ icon: Icon, label, value, sub, tone = "sky", testId }) {
  const tones = {
    sky: { bg: "bg-sky-100", fg: "text-sky-600" },
    emerald: { bg: "bg-emerald-100", fg: "text-emerald-600" },
    amber: { bg: "bg-amber-100", fg: "text-amber-600" },
    rose: { bg: "bg-rose-100", fg: "text-rose-600" },
  };
  const t = tones[tone] || tones.sky;
  return (
    <Card className="medical-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <p className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">{label}</p>
        <div className={`w-8 h-8 rounded-lg ${t.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${t.fg}`} />
        </div>
      </div>
      <p className="font-heading text-3xl font-bold mt-3 text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1.5">{sub}</p>}
    </Card>
  );
}

// Interpolate green→amber→red for heatmap
function heatColor(v, peak) {
  if (!peak || v === 0) return "#f1f5f9";
  const ratio = Math.min(1, v / peak);
  if (ratio < 0.35) {
    // light green to green
    const lerp = ratio / 0.35;
    return `rgba(16, 185, 129, ${0.2 + lerp * 0.4})`;
  }
  if (ratio < 0.7) {
    // green to amber
    return `rgba(245, 158, 11, ${0.5 + (ratio - 0.35) * 0.8})`;
  }
  // amber to red
  return `rgba(220, 38, 38, ${0.75 + (ratio - 0.7) * 0.8})`;
}

const AGE_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#94a3b8"];
const GENDER_COLORS = { male: "#0ea5e9", female: "#ec4899", other: "#8b5cf6", unknown: "#94a3b8" };

export default function PatientsTab({ data }) {
  const k = data?.kpi || {};
  const hm = data?.heatmap || { days: [], hours: [], grid: [], peak: 0 };
  const demo = data?.demographics || { age_groups: [], gender: [] };
  return (
    <div className="space-y-6" data-testid="patients-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-footfall" icon={Users} tone="sky" label="Total Footfall" value={k.footfall || 0} sub="Waiting + checking + completed" />
        <KpiCard testId="kpi-new-vs-returning" icon={UserPlus} tone="emerald" label="New / Returning"
          value={`${k.new_patients || 0} / ${k.returning_patients || 0}`}
          sub={`${(k.new_patients + k.returning_patients) > 0 ? Math.round(((k.returning_patients || 0) / (k.new_patients + k.returning_patients)) * 100) : 0}% returning`} />
        <KpiCard testId="kpi-noshow" icon={TrendingDown} tone={((k.no_show_rate || 0) > 15) ? "rose" : "amber"}
          label="No-Show Rate" value={`${k.no_show_rate || 0}%`} sub={`${k.no_shows || 0} no-shows`} />
        <KpiCard testId="kpi-avg-consult" icon={Clock} tone="sky" label="Avg Consultation"
          value={`${k.avg_consultation_minutes || 0} min`} sub="Started → completed" />
      </div>

      {/* Heatmap */}
      <Card className="medical-card p-5" data-testid="chart-heatmap">
        <div className="mb-3">
          <h4 className="font-heading text-base font-semibold text-slate-900">Peak Hours Heatmap</h4>
          <p className="text-xs text-slate-500">Booking timestamps · 9 AM – 9 PM · darker = busier</p>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="grid" style={{ gridTemplateColumns: `48px repeat(${hm.hours.length}, minmax(0, 1fr))`, gap: "3px" }}>
              <div />
              {hm.hours.map((h) => (
                <div key={h} className="text-[10px] font-medium text-slate-500 text-center py-1">
                  {h > 12 ? `${h - 12}p` : `${h}a`}
                </div>
              ))}
              {hm.days.map((day, di) => (
                <Fragment key={`d-${day}`}>
                  <div className="text-[11px] font-medium text-slate-600 pr-2 flex items-center justify-end">{day}</div>
                  {(hm.grid[di] || []).map((v, hi) => (
                    <div
                      key={`c-${di}-${hi}`}
                      className="rounded aspect-square flex items-center justify-center text-[10px] font-semibold text-slate-800 cursor-default"
                      style={{ backgroundColor: heatColor(v, hm.peak) }}
                      title={`${day} ${hm.hours[hi]}:00 → ${v} bookings`}
                      data-testid={`heat-${di}-${hi}`}
                    >
                      {v > 0 ? v : ""}
                    </div>
                  ))}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 text-[11px] text-slate-500">
          <span>Low</span>
          <div className="flex-1 h-2 rounded-full" style={{ background: "linear-gradient(90deg, #d1fae5, #fef3c7, #fecaca)" }} />
          <span>Peak</span>
        </div>
      </Card>

      {/* Demographics — two side-by-side donuts */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="medical-card p-5" data-testid="chart-age-groups">
          <div className="mb-2">
            <h4 className="font-heading text-base font-semibold text-slate-900">Age Groups</h4>
            <p className="text-xs text-slate-500">Distribution of patients this period</p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={demo.age_groups} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {demo.age_groups.map((_, i) => <Cell key={i} fill={AGE_COLORS[i % AGE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="medical-card p-5" data-testid="chart-gender">
          <div className="mb-2">
            <h4 className="font-heading text-base font-semibold text-slate-900">Gender Split</h4>
            <p className="text-xs text-slate-500">Legacy bookings marked as &quot;Unknown&quot;</p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={demo.gender} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {demo.gender.map((entry, i) => (
                    <Cell key={i} fill={GENDER_COLORS[entry.name] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
